import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button } from 'antd';
import { UserSwitchOutlined } from '@ant-design/icons';
import { useModel } from '@umijs/max';
import { endImpersonation, impersonationEndsAt, isImpersonating } from '@/utils/impersonation';

const RETURN_MARGIN_MS = 5000;

const formatRemaining = (ms: number) => {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

/**
 * Site-wide banner while an admin is logged in as another user ("Log In As
 * User"). Counts down to the token's expiry and switches back to the admin's
 * own account when it runs out, or when they click Return.
 */
const ImpersonationBanner: React.FC = () => {
  const { initialState } = useModel('@@initialState');
  const currentUser = initialState?.currentUser;
  const endsAt = impersonationEndsAt();
  const [now, setNow] = useState(() => Date.now());
  const [ending, setEnding] = useState(false);
  const endedRef = useRef(false);

  const active = isImpersonating() && !!currentUser?.impersonation && !!endsAt;

  const handleEnd = () => {
    if (endedRef.current) return;
    endedRef.current = true;
    setEnding(true);
    endImpersonation();
  };

  useEffect(() => {
    if (!active) return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [active]);

  // Return a few seconds before the token expires, so the user can still be
  // logged out properly (phone offline) with it.
  const returnAt = (endsAt ?? 0) - RETURN_MARGIN_MS;

  useEffect(() => {
    if (active && endsAt && now >= returnAt) handleEnd();
  }, [active, endsAt, now, returnAt]);

  if (!active || !endsAt) return null;

  const userName = currentUser?.display_name || currentUser?.user_name;
  const adminName = currentUser?.impersonation?.by_user_name;

  return (
    <div>
      <Alert
        banner
        type="error"
        showIcon={false}
        message={
          <div style={{ textAlign: 'center', width: '100%' }}>
            <UserSwitchOutlined style={{ marginRight: 8 }} />
            Logged in as <b>{userName}</b>
            {adminName ? <> by {adminName}</> : null}. Changes are recorded on the Log In As log. Ends in{' '}
            <b>{formatRemaining(returnAt - now)}</b>
          </div>
        }
        action={
          <Button size="small" type="primary" danger loading={ending} onClick={handleEnd}>
            Return to my account
          </Button>
        }
      />
    </div>
  );
};

export default ImpersonationBanner;
